import { useMemo, useState } from "react";
import { Form } from "react-bootstrap";

import { DataTable } from "@/components/DataTable";
import { getSosEntityHashId } from "@/components/SosEntityHashLink";
import { TableWrapper } from "@/components/TableWrapper";
import {
  maxPriorityOption,
  PriorityOption,
  priorityOptionColors,
  priorityOptions,
} from "@/features/purchasing-priority/purchasing-priority-utils";
import { useFacetFilter } from "@/hooks/useFacetFilter";
import { withJobTrackingData } from "../withJobTrackingData";
import { getPurchasingProgress } from "./purchasing-progress-data";
import { purchasingProgressTableColumns, PurchasingProgressTableRow } from "./purchasing-progress-table-columns";
import { usePurchasingNotesByItemId } from "./usePurchasingNotesByItemId";

const filterDefs = {
  priority: {
    options: priorityOptions,
    predicate: (row: PurchasingProgressTableRow, filterValue: PriorityOption) =>
      filterValue === maxPriorityOption || // Show everything when the max priority is selected
      !row.component.onSalesOrder || // Items not on the sales order are always shows regardless of priority
      row.component.quantitiesByPriority.get(filterValue)!.quantityRequiredForJob > 0,
  },
};

export const PurchasingProgress = withJobTrackingData(
  ({ job, bomComponents, itemReceipts, purchaseOrders, salesOrderRackBin }) => {
    // Define state and callback for priority selection
    const [priority, setPriority] = useState<PriorityOption>(maxPriorityOption);
    const onPriorityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
      setPriority(parseInt(event.target.value, 10) as PriorityOption);
    };

    // Fetch all purchasing notes for the current job
    const notesByItemIdQuery = usePurchasingNotesByItemId(job.id);

    // Calculate the purchasing progress data
    const purchasingProgress = useMemo(
      () => getPurchasingProgress(bomComponents, itemReceipts, purchaseOrders, salesOrderRackBin),
      [bomComponents, itemReceipts, purchaseOrders, salesOrderRackBin],
    );

    // Combine the purchasing progress data, selected priority, and purchasing notes into table rows
    const data = useMemo(
      () =>
        purchasingProgress.map<PurchasingProgressTableRow>((x) => ({
          ...x,
          rowId: getSosEntityHashId("item", x.component.item.id),
          jobId: job.id,
          quantitiesForSelectedPriority: x.component.quantitiesByPriority.get(priority)!,
          initialNote:
            notesByItemIdQuery.isLoading ? undefined
            : notesByItemIdQuery.error ? notesByItemIdQuery.error
            : (notesByItemIdQuery.data!.get(x.component.item.id) ?? null),
        })),
      [purchasingProgress, job.id, notesByItemIdQuery, priority],
    );

    // Apply filter and get all filter facet quantities
    const { filteredData, facetCounts } = useFacetFilter({
      filterDefs,
      data,
      selectedFilterValues: {
        priority,
      },
    });

    return (
      <>
        <div className="col-auto mb-3">
          <div className="row row-cols-auto gx-3 align-items-center">
            <div>
              <Form.Group
                className="form-floating"
                style={{ "--bs-body-color": "var(--bs-dark)", "--bs-body-color-rgb": "var(--bs-dark-rgb)" }}
              >
                <Form.Select
                  value={priority}
                  onChange={onPriorityChange}
                  size="sm"
                  style={{ backgroundColor: priorityOptionColors[priority] }}
                >
                  {priorityOptions.map((option) => (
                    <option key={option} value={option} style={{ backgroundColor: priorityOptionColors[option] }}>
                      {option} ({facetCounts.priority.get(option) ?? 0} items)
                      {option === maxPriorityOption ? " (All)" : ""}
                    </option>
                  ))}
                </Form.Select>
                <Form.Label>Priority</Form.Label>
              </Form.Group>
            </div>
          </div>
        </div>

        <TableWrapper className="col">
          <DataTable
            bordered
            hover
            data={filteredData}
            columns={purchasingProgressTableColumns}
            getRowId={({ rowId }) => rowId}
            initialState={{
              sorting: [
                { id: "component_item_type", desc: false },
                { id: "component_item_preferredVendor_name", desc: false },
                { id: "component_item_name", desc: false },
              ],
            }}
            meta={{ priority }}
          />
        </TableWrapper>
      </>
    );
  },
);
